import { useState, useEffect, useRef } from "react";
import env from "../../config/env";
import { Row, Col, Input, Checkbox, Button, Modal } from "antd";
import { useHistory } from "react-router-dom";
import { clock } from "../../utils/clock";
import { tillClosingPrint } from "../../lib/printer/tillCloseReceipt";
import CancelArrow from "../../assets/images/cancelArrow.svg";
import db from "../../database";
import data from "../../constants/opentillClosetill.json";
import retailStyles from "../../constants/retailPos.json";
import "../style.css";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import TillCloseHCPrint from "../../lib/printer/tillCloseHCPrinter";
import { getMultilingualText } from "../../utils/getMultilingualText";
import { useTranslation } from "react-i18next";
import TillCloseCWHCPrint from "../../lib/printer/tillCloseCWHCprinter";
import { unlinkTill, upsertPOSActivity, upsertPOSLogService, upsertTill, upsertSupplierPayment, getAggregatorSalesData } from "../../services";
import { executeTillSync } from "../../services/tillCloseService";
import DayEndReportPrint from "../../lib/printer/dayEndReportHCPrinter";
import DayEndReportCWHCPrint from "../../lib/printer/dayEndReportCWHCPrinter";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useAuthStore from "../../stores/useAuthStore";
import useSessionStore from "../../stores/useSessionStore";
import { trace } from "../../utils/debugTrace";
import { clearAuthCookies } from "../../services/authInterceptor";

const CashToKeep = () => {
  const { t } = useTranslation();
  const multilingual = process.env.REACT_APP_MULTILINGUAL === "true" && localStorage.getItem("selectedLanguage") !== "English";
  const tillData = useAuthStore.getState().tillData;
  const tillSession = useSessionStore.getState().tillSession;
  // Capture unlink flag once at mount and clear it immediately — prevents stale flag
  // from surviving crashes and triggering unlink on normal logout/close-shift flows.
  // Safe to reset synchronously here now that executeTillSync's unlink decision uses
  // this captured `shouldUnlink` snapshot (passed down as a prop) instead of a live
  // useSessionStore.getState().unlink read — so resetting the store's flag immediately
  // no longer races with (or defeats) the async close-till flow. `unlink` is persisted
  // via sqliteImmediateStorage, so an unreset flag survives localStorage.clear() /
  // db.tables.clear() and silently re-triggers a full unlink on every later close-till.
  const [shouldUnlink] = useState(() => {
    const flag = useSessionStore.getState().unlink;
    if (flag) useSessionStore.getState().setUnlink(false);
    return flag;
  });
  const domainURL = process.env.REACT_APP_domain;
  const [allExpectedAmount, setAllExpectedAmount] = useState(0);
  const [allTransactionCount, setAllTransactionCount] = useState(0);
  const [paymentsList, setPaymentsList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [allPaymentData, setAllPaymentData] = useState();
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const sessionId = tillSession?.tillSessionId;

  // Hide "Close Store" while operating a next-business-date session on an earlier
  // calendar day (store already day-closed for today, reopened for the next business
  // date). Day-close must wait until the calendar date reaches the session's business
  // date. Cross-midnight day-close has businessDate < today, so it is unaffected and
  // still shows the button; legacy sessions without businessDate default to showing it.
  const sessionBusinessDate = tillSession?.businessDate ? dayjs(tillSession.businessDate).format("YYYY-MM-DD") : null;
  const isFutureBusinessDateSession = sessionBusinessDate ? sessionBusinessDate > dayjs().format("YYYY-MM-DD") : false;
  const [cashIndex, setCashIndex] = useState(false);
  const [cashSaleAmount, setCashSaleAmount] = useState(0);
  const [noteValue, setNoteValue] = useState("");
  const inputRef = useRef(null);
  const [aggregatorSalesData, setAggregatorSalesData] = useState(null);
  const [cashToKeep, setCashToKeep] = useState(() => {
    return posConfig?.manualCashToKeep === "Y" ? useSessionStore.getState().tillSession?.totalOpeningAmount || 0 : 0;
  });
  const [cashValues, setCashValues] = useState({
    cashIn: 0,
    cashOut: 0,
    pettyCashIn: 0,
    pettyCashOut: 0,
  });
  const ObFlag = tillData?.tillAccess?.cwrTill?.tillCloseTemplate?.obController === "Y" ? true : false;
  const printDayEndReport = process.env.REACT_APP_PRINT_DAY_END_REPORT === "true";
  const CWHardwareController = tillData?.tillAccess?.cwrTill?.printTemplate?.cwController === "Y" ? true : false;
  // TSK-3139: Day End Report gets its own template/flags (storeCloseTemplate), independent of
  // the sales receipt (printTemplate) and till-close receipt (tillCloseTemplate) flags above.
  // When a till has no storeCloseTemplate configured (both controller fields null), fall back
  // to the legacy ObFlag/CWHardwareController + hardcoded template — zero regression for
  // existing tenants (e.g. Chocolala Ajman 2) that never got the new field populated.
  const closeStoreTemplate = tillData?.tillAccess?.cwrTill?.storeCloseTemplate;
  const isCloseStoreTemplateConfigured = !!(closeStoreTemplate?.obController || closeStoreTemplate?.cwController);
  const CloseStoreObFlag = isCloseStoreTemplateConfigured ? closeStoreTemplate?.obController === "Y" : ObFlag;
  const CloseStoreCWFlag = isCloseStoreTemplateConfigured ? closeStoreTemplate?.cwController === "Y" : CWHardwareController;
  // Initialized from the persisted store value (not just false/"") so that a
  // browser refresh while the Day End Application is open restores it here
  // instead of dropping the user back onto the interactive Close Till screen.
  // The lock is only ever set right before tillSession is nulled (see
  // handleOpenDayEndApp / doLogout), so a genuine refresh-recovery always has
  // tillSession === null. A landing here WITH a live tillSession is a normal,
  // fresh close-till visit — any dayEndAppUrl still set at that point is a
  // leftover from an earlier abandoned/test attempt, not something to resume,
  // so drop it instead of re-trapping an unrelated visit.
  const [isIframeModalVisible, setIsIframeModalVisible] = useState(() => {
    const persistedUrl = useSessionStore.getState().dayEndAppUrl;
    if (persistedUrl && tillSession) {
      useSessionStore.getState().setDayEndAppUrl(null);
      return false;
    }
    return !!persistedUrl;
  });
  const [iframeUrl, setIframeUrl] = useState(() => {
    const persistedUrl = useSessionStore.getState().dayEndAppUrl;
    return persistedUrl && !tillSession ? persistedUrl : "";
  });

  const handleOpenDayEndApp = (url) => {
    useSessionStore.getState().setDayEndAppUrl(url);
    setIframeUrl(url);
    setIsIframeModalVisible(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      let obj = cashValues;
      let payments = [];
      try {
        // A restored Day End Application lock means the till was already closed
        // (tillSession/tillData are legitimately gone) — stay put and let the
        // modal render instead of bouncing to /till.
        if (isIframeModalVisible) return;
        if (!tillData || !tillSession || !tillSession.allPaymentsData) {
          history.push("/till");
          return;
        }
        if (tillSession.cashInOutData) {
          setCashValues(tillSession.cashInOutData);
          obj = tillSession.cashInOutData;
        }
        if (tillSession) {
          payments = (tillSession.allPaymentsData || []).map((payment) => {
            if (payment.name.toLowerCase() !== "cash") {
              return {
                ...payment,
                amount: payment.amount > 0 ? payment.amount : payment.expectedAmount,
                actualAmount: payment.expectedAmount,
                difference: 0,
              };
            }
            return { ...payment };
          });
        }
        await db.tillEvents
          .where("tillSessionId")
          .equals(sessionId)
          .toArray()
          .then((tillEvent) => {
            if (tillEvent.length > 0) {
              tillEvent[0].allPaymentsData.map((payment) => {
                if (payment.name.toLowerCase() !== "cash") {
                  payment.amount = payment.amount > 0 ? payment.amount : payment.expectedAmount;
                  payment.actualAmount = payment.actualAmount > 0 ? payment.actualAmount : payment.expectedAmount;
                  // payment.difference = 0;
                  payment.difference = payment.difference > 0 ? payment.difference : 0;
                }
              });
              payments = tillEvent[0].allPaymentsData;
            }
          });
      } catch (error) {
        console.error("Error fetching till events:", error);
      }
      const cashPaymentIndex = payments?.findIndex((apd) => apd.name.toLowerCase() === "cash");
      setCashIndex(cashPaymentIndex === -1 ? true : false);
      if (posConfig?.showDenominations === "N") {
        try {
          const orders = await db.orders.where("tillSessionId").equals(sessionId).toArray();
          const updatedTillSession = { ...tillSession };
          const updatedAllPaymentsData = [...payments];
          if (cashPaymentIndex !== -1) {
            updatedAllPaymentsData[cashPaymentIndex].amount = totalAmount;
            updatedAllPaymentsData[cashPaymentIndex].expectedAmount = parseFloat(
              (updatedAllPaymentsData[cashPaymentIndex].expectedAmount - updatedAllPaymentsData[cashPaymentIndex].paymentReturn).toFixed(2)
            );
            updatedAllPaymentsData[cashPaymentIndex].cashSaleAmount = cashSaleAmount;
            updatedAllPaymentsData[cashPaymentIndex].difference = parseFloat(
              (updatedAllPaymentsData[cashPaymentIndex].amount - updatedAllPaymentsData[cashPaymentIndex].expectedAmount).toFixed(2)
            );
          }
          // BUG-2019: closingTime intentionally not stamped here — this effect runs on
          // mount of the Close screen, before Close Store/Close Shift is clicked. See
          // excuteCloseTillAll below for the single place it is now set, at confirm time.
          updatedTillSession.closingCash = totalAmount;
          updatedTillSession.closingCashDenominations = [];
          updatedTillSession.allPaymentsData = updatedAllPaymentsData;
          updatedTillSession.totalOrdersCount = orders.length;
          await db.tillEvents.where("tillSessionId").equals(updatedTillSession.tillSessionId).modify(updatedTillSession);
          useSessionStore.getState().setTillSession(updatedTillSession);
          setAllExpectedAmount(updatedAllPaymentsData[cashPaymentIndex]?.expectedAmount ?? 0);
          setAllTransactionCount(updatedAllPaymentsData[cashPaymentIndex]?.transactionCount ?? 0);
          setTotalAmount(updatedAllPaymentsData[cashPaymentIndex]?.amount ?? 0);
          setPaymentsList([...payments]);
          setAllPaymentData(updatedTillSession.allPaymentsData);
        } catch (error) {
          console.error("Error fetching orders or updating till session:", error);
        }
      } else if (posConfig?.showDenominations === "Y") {
        const tillSessionPayments = (tillSession?.allPaymentsData ?? []).map((p) => ({ ...p }));
        let totalAmountAllPayments = 0;
        let transactionCount = 0;
        let total = 0;
        let salesAmount = 0;
        tillSessionPayments?.forEach((pay) => {
          totalAmountAllPayments += pay?.expectedAmount ?? 0;
          transactionCount += pay?.transactionCount ?? 0;
          if (pay?.name?.toLowerCase() === "cash") {
            salesAmount += pay?.cashSaleAmount ?? 0;
            pay.cashSaleAmount = pay?.expectedAmount ?? 0;
          } else {
            salesAmount += pay?.expectedAmount ?? 0;
          }
          // If CloseTIllAll.js already netted paymentReturn out of expectedAmount for this
          // row (pay.returnAdjusted), don't subtract it again — that double-nets returns.
          if (pay?.name?.toLowerCase() === "cash") {
            if (!pay?.returnAdjusted) {
              pay.expectedAmount = (pay?.expectedAmount ?? 0) - (pay?.paymentReturn ?? 0);
            }
          } else {
            pay.expectedAmount = (pay?.expectedAmount ?? 0) - (pay?.paymentReturn ?? 0);
          }
          total += parseFloat(pay?.actualAmount ?? 0) >= 0 ? parseFloat(pay?.actualAmount) : 0;
        });
        const openAmount = useSessionStore.getState().tillSession?.totalOpeningAmount ?? 0;
        payments.map((ele) => {
          if (ele.name.toLowerCase() !== "cash") {
            ele.saleAmount = ele.difference > 0 ? parseFloat(parseFloat(ele.expectedAmount + ele.paymentReturn).toFixed(2)) : parseFloat(parseFloat(ele.expectedAmount).toFixed(2));
          }
          if (ele.name.toLowerCase() !== "cash") {
            ele.expectedAmount =
              ele.difference > 0 ? parseFloat(parseFloat(ele.expectedAmount).toFixed(2)) : parseFloat(parseFloat(ele.expectedAmount - ele.paymentReturn).toFixed(2));
          } else {
            // `ele.returnAdjusted` (set by CloseTIllAll.js) is the reliable signal that
            // paymentReturn was already netted out — NOT the coincidental amount===expectedAmount
            // check this used to rely on, which double-netted returns whenever CloseTIllAll.js
            // had already run this same subtraction upstream.
            ele.expectedAmount = ele.returnAdjusted
              ? parseFloat(parseFloat(ele.expectedAmount).toFixed(2))
              : parseFloat(parseFloat(ele.expectedAmount - ele.paymentReturn).toFixed(2));
          }
          if (ele.name.toLowerCase() !== "cash") {
            ele.amount = ele.difference === 0 ? parseFloat(parseFloat(ele.amount - ele.paymentReturn).toFixed(2)) : parseFloat(parseFloat(ele.actualAmount).toFixed(2));
          }
        });
        totalAmountAllPayments = salesAmount + obj?.cashIn - obj?.cashOut + obj?.pettyCashIn - obj?.pettyCashOut + openAmount;
        setAllPaymentData(tillSessionPayments);
        setAllExpectedAmount(totalAmountAllPayments);
        setAllTransactionCount(transactionCount);
        setTotalAmount(total);
        setPaymentsList([...payments]);
      }
    };

    fetchData();

    // Fetch aggregator (Swiggy/Zomato) sales data if applicable
    if (isIframeModalVisible) return;
    (async () => {
      try {
        const posSaleTypes = await db.posSaletypes.toArray();
        const hasAggregator = posSaleTypes.some((st) => st.cwrSaletype?.isAggregator === "Y");
        if (posConfig?.disableOfflineBilling === "Y" && hasAggregator) {
          const tillEventsArr = await db.tillEvents.where("tillStatus").equals("open").toArray();
          const currentDate = tillEventsArr[0]?.openingTime ? new Date(tillEventsArr[0].openingTime).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
          const aggData = await getAggregatorSalesData(tillData?.tillAccess?.csBunit?.csBunitId, currentDate);
          setAggregatorSalesData(aggData);
        }
      } catch (e) {
        console.error("Failed to fetch aggregator data", e);
      }
    })();
  }, []);

  const history = useHistory();
  const currentDate = new Date().toLocaleDateString();
  const [displayClock, setDisplayClock] = useState("");
  const [checkedValue, setCheckedValue] = useState("0");
  // Tracks which action is in flight (null | "store" | "shift") so the Close Store and
  // Close Shift buttons each show their own loading/disabled state — a single shared
  // boolean here previously made clicking Close Store also flip Close Shift into its
  // "Closing..." loading label, even though Close Shift was never clicked.
  const [closingAction, setClosingAction] = useState(null);
  const isButtonDisabled = closingAction !== null;

  useEffect(() => {
    setDisplayClock(clock());
    const timerId = setInterval(() => setDisplayClock(clock()), 1000);
    return () => {
      clearInterval(timerId);
    };
  }, []);

  const onChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const totalOpeningAmount = useSessionStore.getState().tillSession?.totalOpeningAmount || 0;
      setCashToKeep(totalOpeningAmount);
      setCheckedValue("0");
    } else {
      setCashToKeep(0);
      setCheckedValue("");
    }
  };

  let reconstructedObject;

  const rawtillAccessMeta = tillData?.tillAccess?.tillAccessMeta;
  if (rawtillAccessMeta) {
    const rawDataArray = tillData?.tillAccess?.tillAccessMeta;

    // Find the object with the key "Open Till"
    const closeTillObject = rawDataArray.find((item) => item.key === "Close Till");

    // Access the value property of the found object
    const closeTillValue = closeTillObject?.value;

    if (closeTillValue !== undefined) {
      // The rest of your code that depends on closeTillValue
      // Clean up the string (remove extra characters)
      const cleanedData = closeTillValue.replace(/[{}"]/g, "");

      // Split the string into key-value pairs
      const keyValuePairs = cleanedData.split(",");

      // Create an object from key-value pairs
      reconstructedObject = {};
      keyValuePairs.forEach((pair) => {
        // Check if the pair contains a colon
        if (pair.includes(":")) {
          const [key, value] = pair.split(":");

          // Check if both key and value are not undefined before trimming
          if (key !== undefined && value !== undefined) {
            reconstructedObject[key.trim()] = value.trim();
          }
        }
      });
    } else {
      // Handle the case where closeTillValue is undefined
      console.error("closeTillValue is undefined");
    }
  }

  // const clearCookiesAndLocalStorage = () => {
  //   const cookies = document.cookie.split(";");
  //   const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  //   const domainPart = isLocalhost ? "" : `domain=${domainURL};`;
  //   const securePart = isLocalhost ? "" : "secure=true;";
  //   for (const cookie of cookies) {
  //     const eqPos = cookie.indexOf("=");
  //     const name = (eqPos > -1 ? cookie.substr(0, eqPos) : cookie).trim();
  //     document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${securePart}SameSite=Strict;${domainPart}`;
  //   }
  //   useSessionStore.getState().setTillSession(null);
  //   useSessionStore.getState().setNextBusinessDate(null);
  //   localStorage.removeItem("tillSession");
  //   localStorage.removeItem("nextBusinessDate");
  //   window.electronAPI?.auth?.logout();
  //   window.location.assign("/sign-in");
  // };

  // Clears every browser cookie (accessToken, Enterprise.accessToken, username, etc).
  // Must run on every real logout — if session cookies survive, the SSO bootstrap on
  // /sign-in (Authentication/index.js `check()`) reads them back and silently
  // re-authenticates the same user, which looks like "Close Store logs you back in".
  // BUG-2334: this used to delete cookies against a single guessed domain
  // (domain=${domainURL}), which misses a cookie actually set host-only or under a
  // different domain variant — the same host-only-cookie failure mode BUG-2026
  // already hardened authInterceptor.js's clearAuthCookies() against (4 domain
  // variants). Delegate to that instead of keeping a second, weaker implementation.
  const clearCookies = () => {
    clearAuthCookies();
  };

  const clearCookiesAndLocalStorage = async (closeStoreFlag) => {
    const dayEndUrl = env.dayEndApplicationUrl;
    // The day-end app loads in an iframe on this same page and needs the session
    // cookie to authenticate its own requests — cookies are preserved only in that
    // one case. Every other exit is a real logout and must clear them.
    const openingDayEndApp = posConfig?.advanceDayClose === "Y" && closeStoreFlag && !!dayEndUrl;

    if (!openingDayEndApp) {
      clearCookies();
    }

    useSessionStore.getState().setTillSession(null);
    useSessionStore.getState().setNextBusinessDate(null);

    localStorage.removeItem("tillSession");
    localStorage.removeItem("nextBusinessDate");

    if (posConfig?.advanceDayClose === "Y" && closeStoreFlag) {
      if (dayEndUrl) {
        handleOpenDayEndApp(dayEndUrl);
      } else {
        window.location.assign("/");
        console.error("Day end application URL is not defined");
      }
    } else {
      useAuthStore.getState().logout();
      // BUG-2026: await the shell's logout IPC before navigating — it's the
      // authoritative teardown; firing-and-forgetting it raced the redirect.
      if (window.electronAPI?.auth) {
        await window.electronAPI.auth.logout().catch(() => {});
      }
      window.location.assign("/sign-in");
    }
  };

  const excuteCloseTillAll = async (closeStoreFlag) => {
    // Guard + disable must happen synchronously, before any `await`, so the buttons are
    // already disabled by the time this tick ends — otherwise a rapid click on the other
    // button during the aggregator-data fetch below could start a second concurrent close
    // flow before React re-renders with the disabled state.
    if (isButtonDisabled) return;
    setClosingAction(closeStoreFlag ? "store" : "shift");
    // BUG-2334: set by onSyncFailure below when executeTillSync's upsertTill call
    // actually failed — read afterward to skip the unconditional-success DCL log.
    const syncFailedRef = { current: false };

    // Sales rep early-exit: if current user matches a sales rep, perform quick
    // cookie/localStorage cleanup and redirect without the full close flow.
    // This handles non-primary users who should not trigger till sync.
    const username = localStorage.getItem("username") || useAuthStore.getState().userData?.user;
    const salesReps = tillData?.tillAccess?.csBunit?.salesRep || [];
    const matchedRep = salesReps.find((rep) => rep.name === username);
    if (matchedRep) {
      clearCookiesAndLocalStorage(closeStoreFlag);
      return;
    }

    // Resolve aggregator sales data (re-fetch if needed)
    let resolvedAggregatorData = aggregatorSalesData;
    const posSaleTypes = await db.posSaletypes.toArray();
    const hasAggregator = posSaleTypes.some((st) => st.cwrSaletype?.isAggregator === "Y");
    if (posConfig?.disableOfflineBilling === "Y" && hasAggregator && !resolvedAggregatorData) {
      try {
        const tillEventsArr = await db.tillEvents.where("tillStatus").equals("open").toArray();
        const currentDate = tillEventsArr[0]?.openingTime ? new Date(tillEventsArr[0].openingTime).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
        resolvedAggregatorData = await getAggregatorSalesData(tillData?.tillAccess?.csBunit?.csBunitId, currentDate);
      } catch (e) {
        console.error("Failed to re-fetch aggregator data", e);
      }
    }

    trace("CashToKeep", "excuteCloseTillAll start", {
      closeStoreFlag,
      hasTillAccess: !!tillData?.tillAccess,
      hasCsBunit: !!tillData?.tillAccess?.csBunit,
      hasTillSession: !!tillSession,
    });
    if (!tillData?.tillAccess) {
      trace("CashToKeep", "tillData.tillAccess missing → /till");
      console.error("tillData missing — cannot close shift");
      history.push("/till");
      return;
    }
    tillSession.allPaymentsData = allPaymentData;
    useSessionStore.getState().setTillSession(tillSession);
    await db.tillEvents
      .where("tillSessionId")
      .equals(tillSession.tillSessionId)
      .modify((tillEvent) => {
        tillEvent.allPaymentsData = allPaymentData;
      });
    const updatedPaymentsList = paymentsList;
    let closingSession = tillSession;
    await db.tillEvents
      .where("tillStatus")
      .equalsIgnoreCase("open")
      .toArray()
      .then((tillEvent) => {
        if (tillEvent.length > 0) {
          closingSession = tillEvent[0];
          closingSession.checkedValue = checkedValue === "0" && posConfig?.manualCashToKeep === "N" ? useSessionStore.getState().tillSession?.totalOpeningAmount : cashToKeep;
          if (closingSession.checkedValue === "") {
            closingSession.checkedValue = 0;
          }
        }
      });
    closingSession.totalClosingExpectedAmount = parseFloat(allExpectedAmount.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision));
    closingSession.totalClosingTransactions = parseFloat(allTransactionCount.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision));
    closingSession.totalClosingAmount = parseFloat(totalAmount.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision));
    closingSession.allPaymentsData = updatedPaymentsList;
    closingSession.isSynced = 0;
    const cashPaymentIndex = updatedPaymentsList?.findIndex((apd) => apd.name.toLowerCase() === "cash");
    const retainAmt = checkedValue === "0" && posConfig?.manualCashToKeep === "N" ? useSessionStore.getState().tillSession?.totalOpeningAmount : cashToKeep || 0;
    if (cashPaymentIndex !== -1) {
      closingSession.allPaymentsData[cashPaymentIndex].amount = closingSession.closingCash;
    }
    closingSession.closingCash = closingSession.closingCash - retainAmt;
    // BUG-2019: closingTime is stamped here, at the moment the user actually confirms
    // (Close Store / Close Shift), instead of on each screen's mount. This is also the
    // earliest point the receipt/day-end report can legitimately show a closing time —
    // any earlier and an abandoned close-till flow would leave the till record showing
    // tillStatus "open" with a closing time already filled in.
    closingSession.closingTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");

    await db.tillEvents.where("tillSessionId").equals(closingSession.tillSessionId).modify(closingSession);

    if (closeStoreFlag) {
      // Use the current session's opening date as the business date for the day-end report.
      // This handles cross-midnight correctly: a till opened before midnight reports under
      // its opening date, not the current system date.
      const sessionBusinessDate = tillSession?.openingTime?.split(" ")[0] || new Date().toISOString().split("T")[0];
      const allTillEvents = await db.tillEvents.toArray();
      const todaysTillEvents = allTillEvents.filter((tillEvent) => {
        const eventDate = tillEvent.openingTime?.split(" ")[0];
        return eventDate === sessionBusinessDate;
      });
      let totalSystemClosing = 0;

      todaysTillEvents.forEach((event) => {
        const cashPayment = event.allPaymentsData?.find((p) => p.name?.toLowerCase() === "cash");

        if (!cashPayment) return;

        const expected = Number(cashPayment.expectedAmount || 0);
        const returnAmt = Number(cashPayment.paymentReturn || 0);

        const cio = event.cashInOutData || {};

        const cashIn = Number(cio.cashIn || 0);
        const cashOut = Number(cio.cashOut || 0);
        const pettyIn = Number(cio.pettCashIn || 0);
        const pettyOut = Number(cio.pettCashOut || 0);

        // BUG-1631-class double-netting: CloseTIllAll.js (this same close-till flow, one screen
        // earlier) already subtracts paymentReturn out of expectedAmount and stamps
        // returnAdjusted=true on the cash row before it reaches db.tillEvents — the same guard
        // cashToKeep.js's own showDenominations==="Y" branch above (and CloseTIllAll.js's payments
        // loop) already checks. This loop read event.allPaymentsData straight from db.tillEvents
        // and subtracted returnAmt unconditionally, netting the return out a second time and
        // understating the Day End Report's System/Closing Balance by the return amount (confirmed
        // live: a session with a clean 1.00 net cash position printed System 0.750/Diff -0.250,
        // off by exactly its 0.25 return).
        const system = (cashPayment.returnAdjusted ? expected : expected - returnAmt) + cashIn + pettyIn - pettyOut - cashOut;

        totalSystemClosing += system;
      });
      const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;
      const paymentTotals = {};
      todaysTillEvents.forEach((event) => {
        event.allPaymentsData?.forEach((payment) => {
          const methodId = payment.finPaymentmethodId;
          const amount = payment.name.toLowerCase() === "cash" ? Number(payment.cashSaleAmount || 0) : Number(payment.amount || 0);
          const transactionCount = payment.transactionCount;
          if (!paymentTotals[methodId]) {
            paymentTotals[methodId] = {
              finPaymentmethodId: methodId,
              paymentName: payment.name || "",
              totalAmount: 0,
              orderCount: 0,
            };
          }
          paymentTotals[methodId].totalAmount += amount;
          paymentTotals[methodId].orderCount += transactionCount;
        });
      });
      const paymentSummary = Object.values(paymentTotals).map((p) => ({
        ...p,
        totalAmount: round2(p.totalAmount),
      }));
      const paymentSummaryTotal = round2(paymentSummary.reduce((sum, p) => sum + p.totalAmount, 0));

      const denominationSummary = todaysTillEvents.reduce((acc, event) => {
        (event.closingCashDenominations || []).forEach((denomination) => {
          const { value, seqNo, count, amount } = denomination;
          const existing = acc.find((d) => d.value === value && d.seqNo === seqNo);

          if (existing) {
            existing.count += count;
            existing.amount += amount;
          } else {
            acc.push({
              value,
              seqNo,
              count,
              amount,
            });
          }
        });
        return acc;
      }, []);
      denominationSummary.sort((a, b) => a.seqNo - b.seqNo);
      const denominationsTotal = denominationSummary.reduce((sum, d) => sum + d.amount, 0);

      // BUG-1913: closingCashDenominations is intentionally left empty for any session closed
      // with posConfig.showDenominations === "N" (manual total entry instead of note counting —
      // see this file's own showDenominations==="N" branch above, which stores the counted
      // total in closingCash instead). denominationsTotal alone then reads 0 for those sessions,
      // so the Cash Account row showed a false "Actual" of 0 and a bogus Diff even when the till
      // balanced. Fall back to the session's manually-entered closingCash whenever it recorded
      // no denominations, so the report's Cash Account reflects what was actually counted.
      const actualCashCounted = todaysTillEvents.reduce((sum, event) => {
        const eventDenominations = event.closingCashDenominations || [];
        const eventTotal = eventDenominations.length > 0 ? eventDenominations.reduce((s, d) => s + (d.amount || 0), 0) : Number(event.closingCash || 0);
        return sum + eventTotal;
      }, 0);

      const businessDateFormatted = dayjs(sessionBusinessDate, "YYYY-MM-DD").format("DD-MM-YYYY");
      // Filter by today's till session IDs to avoid loading all historical orders.
      // todaysTillEvents is already filtered to today's business date above.
      const todaySessionIds = todaysTillEvents.map((e) => e.tillSessionId).filter(Boolean);

      // Source Tax Details/Gross Total from db.ordersData's per-session salesTax/returnTax —
      // the same pre-aggregated, already-return-tracked table tillCloseCWHCprinter.js reads
      // (ordersData[0].salesTax/returnTax) for the Close Till report's Net/Gross Sales and
      // Net/Gross Returns lines. Previously this summed raw db.orders[].taxDetails directly,
      // which didn't net sales against returns the way Close Till's "Total Retail
      // Transactions" (Gross Sales − Gross Returns) does — a same-session sale-then-full-return
      // showed a nonzero negative Gross Total on the Day End report while Close Till correctly
      // showed 0.00 for the identical transactions.
      const todaysOrdersData = todaySessionIds.length > 0 ? await db.ordersData.where("tillSessionId").anyOf(todaySessionIds).toArray() : [];

      const taxSummary = {};
      todaysOrdersData.forEach((orderData) => {
        // returnTax entries are already stored with a negative taxAmt/taxableAmt (mirrors a
        // return line's negative taxAmount/nettotal), so summing salesTax and returnTax
        // together nets them per rate instead of leaving the return unnetted.
        [...(orderData.salesTax || []), ...(orderData.returnTax || [])].forEach((tax) => {
          const taxKey = `VAT - ${tax.taxRate}%`;

          if (!taxSummary[taxKey]) {
            taxSummary[taxKey] = {
              taxType: taxKey,
              net: 0,
              tax: 0,
            };
          }

          taxSummary[taxKey].net += Number(tax.taxableAmt || 0);
          taxSummary[taxKey].tax += Number(tax.taxAmt || 0);
        });
      });
      const taxDetailsSummary = Object.values(taxSummary).map((row) => ({
        taxType: row.taxType,
        net: round2(row.net),
        tax: round2(row.tax),
      }));

      const totalNet = round2(taxDetailsSummary.reduce((sum, row) => sum + row.net, 0));
      const totalTax = round2(taxDetailsSummary.reduce((sum, row) => sum + row.tax, 0));
      const taxGrossTotal = round2(totalNet + totalTax);

      const totalCashSaleAmount = todaysTillEvents.reduce((acc, event) => {
        const cashPayments = event.allPaymentsData?.filter((payment) => payment.name?.toLowerCase() === "cash") || [];

        const eventCashTotal = cashPayments.reduce((sum, payment) => sum + Number(payment.cashSaleAmount || 0), 0);

        return acc + eventCashTotal;
      }, 0);
      const cashInOutTotals = todaysTillEvents.reduce(
        (acc, event) => {
          const cio = event.cashInOutData || {};

          acc.cashIn += Number(cio.cashIn || 0);
          acc.cashOut += Number(cio.cashOut || 0);
          acc.pettCashIn += Number(cio.pettCashIn || 0);
          acc.pettCashOut += Number(cio.pettCashOut || 0);

          return acc;
        },
        { cashIn: 0, cashOut: 0, pettCashIn: 0, pettCashOut: 0 }
      );

      const tillValue = useAuthStore.getState().tillValue;
      const dayEndReport = {
        openedAt: todaysTillEvents[0]?.openingTime || null,
        closedAt: todaysTillEvents[todaysTillEvents.length - 1]?.closingTime || null,
        date: businessDateFormatted,
        paymentsSummary: paymentSummary,
        paymentSummaryTotal: paymentSummaryTotal,
        denominationsSummary: denominationSummary,
        denominationsTotal: denominationsTotal,
        taxDetailsSummary: taxDetailsSummary,
        taxGrossTotal: taxGrossTotal,
        branch: tillValue?.name,
        cashSales: totalCashSaleAmount,
        userName: todaysTillEvents[todaysTillEvents.length - 1]?.user || "",
        openingBalance: todaysTillEvents[todaysTillEvents.length - 1]?.openingCash || 0,
        cashInOutSummary: cashInOutTotals,
        systemClosingbalance: totalSystemClosing,
        closingBalance: actualCashCounted,
        closingBalanceDiff: parseFloat((totalSystemClosing - actualCashCounted).toFixed(tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision)),
        closingBalanceCarryForward: cashToKeep,
      };

      if (printDayEndReport) {
        if (CloseStoreObFlag) {
          await DayEndReportPrint(dayEndReport, closeStoreTemplate?.xmlcode2);
        } else if (CloseStoreCWFlag) {
          await DayEndReportCWHCPrint(dayEndReport, closeStoreTemplate?.xmlcode2);
        }
      }
    }

    // Sync to server → print receipt → logout (all handled by tillCloseService)
    await executeTillSync({
      closingSession,
      tillData,
      tillSession,
      closeStoreFlag,
      noteValue,
      cashToKeep,
      checkedValue,
      cashValues,
      paymentsList,
      shouldUnlink,
      ObFlag,
      CWHardwareController,
      posConfig,
      domainURL,
      unlinkTillFn: unlinkTillAPI,
      aggregatorSalesData: resolvedAggregatorData,
      onDayEndOpen: handleOpenDayEndApp,
      clearCookiesAndLocalStorageFn: clearCookiesAndLocalStorage,
      // BUG-2334: a real upsertTill failure (e.g. "cannot close the store — open
      // tills") used to fall through to doLogout() exactly like a success, opening
      // the Day-End-App / logging out for a store that never actually closed, with
      // no message shown. Surface it instead and leave the buttons enabled to retry.
      onSyncFailure: (message) => {
        syncFailedRef.current = true;
        setClosingAction(null);
        errorMessage("Close Failed", sanitizeSyncErrorMessage(message));
      },
    });

    // BUG-2334: this DCL audit-log write hardcodes status "SCS" (success)
    // unconditionally — it used to fire even when the sync above actually failed,
    // recording the store as closed in cwr_pos_log when it never closed (confirmed
    // live: the DCL entry is what tillCloseService.js's Step 3 fallback reached
    // regardless of upsertTill's result). Skip it when onSyncFailure fired.
    if (syncFailedRef.current) {
      return;
    }
    if (usePOSConfigStore.getState().posConfig?.DCL === "Y") {
      const timeMark = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
      const currentDate = new Date().toLocaleDateString("zh-Hans-CN");
      const dayClosingArray = [];
      const trxId = uuidv4().replace(/-/g, "").toUpperCase();
      dayClosingArray.push(`{
              type: "DCL",
              action: "LOG",
              description: "${timeMark}",
              date: "${currentDate}",
              time: "${timeMark}",
              orderNo: "",
              remarks: "",
              transactionId: "${trxId}"
              status: "SCS"
              duration: null
            }`);
      upsertPOSLogService(tillData.tillAccess.cwrTill.cwrTillID, tillData.tillAccess.csUserId, tillData.tillAccess.csBunit.csBunitId, dayClosingArray).catch(() => {});
    }
  };

  // excuteCloseTillAll normally ends by navigating away (logout/unlink), so closingAction
  // is never reset on success — but if it throws before reaching that point (e.g. unexpected
  // data shape), the rejection would otherwise go unhandled and leave both buttons stuck
  // disabled/"Closing..." forever with no way to retry. Reset + surface the failure here.
  const runCloseAction = (closeStoreFlag) => {
    excuteCloseTillAll(closeStoreFlag).catch((error) => {
      console.error("[CashToKeep] Close action failed:", error?.message || error);
      trace("CashToKeep", "excuteCloseTillAll failed", { closeStoreFlag, error: error?.message });
      setClosingAction(null);
      errorMessage("Close Failed", "Something went wrong while closing. Please try again.");
    });
  };

  const unlinkTillAPI = async (closeStoreFlag) => {
    try {
      let id = tillData?.tillAccess?.cwrTill?.cwrTillID;

      // Release the till on the server BEFORE clearing local state.
      // When this was fire-and-forget, a slow network left the till as "INU" on
      // the server while the user was already on the next business unit's till
      // selection screen, making it impossible to select/unlink the till.
      if (id) {
        await unlinkTill(id).catch((err) => console.warn("Server unlink failed:", err));
      }

      useSessionStore.getState().setTillSession(null);
      useAuthStore.getState().unlinkTill();
      localStorage.clear();
      if (db?.tables) {
        await Promise.all(db.tables.map((table) => table.clear())).catch(() => {});
      }

      // BUG-2026: await the shell's logout IPC before navigating — it's the
      // authoritative teardown; firing-and-forgetting it raced the redirect.
      await window.electronAPI?.auth?.logout();
      const dayEndUrl = env.dayEndApplicationUrl;
      const openingDayEndApp = posConfig?.advanceDayClose === "Y" && closeStoreFlag && !!dayEndUrl;
      if (!openingDayEndApp) {
        clearCookies();
      }
      if (openingDayEndApp) {
        handleOpenDayEndApp(dayEndUrl);
      } else {
        window.location.assign("/sign-in");
      }
    } catch (error) {
      console.error("Error in unlinkTillAPI:", error);
      clearCookies();
      await window.electronAPI?.auth?.logout();
      window.location.assign("/sign-in");
    }
  };

  const handleCashToKeepChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9.]/g, "");
    if (value.startsWith(".")) {
      value = value.substring(1);
    }
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }
    setCashToKeep(value);
  };

  const handleAddAmount = (value) => {
    if (cashIndex) return;
    if (checkedValue === "0" && posConfig?.manualCashToKeep === "Y") {
      if (value === ".") {
        if (!cashToKeep || cashToKeep.includes(".")) return;
      }
      if (value === "clear") {
        setCashToKeep(0);
      } else if (value === "x") {
        setCashToKeep((prev) => `${prev.toString().substring(0, prev.toString().length - 1)}`);
      } else {
        const inputElement = inputRef?.current?.input;
        const selectionStart = inputElement.selectionStart;
        const selectionEnd = inputElement.selectionEnd;

        if (selectionStart !== selectionEnd) {
          const newValue = `${inputElement.value.substring(0, selectionStart)}${value}${inputElement.value.substring(selectionEnd)}`;
          setCashToKeep(newValue);
        } else {
          setCashToKeep((prev) => `${prev}${value}`);
        }
      }
    }
  };

  useEffect(() => {
    if (inputRef?.current && checkedValue === "0") {
      inputRef.current.select();
    }
  }, [checkedValue]);

  const errorMessage = (modalTitle, modalContent, user) => {
    Modal.error({
      title: modalTitle,
      content: modalContent,
    });
  };

  // BUG-2334: upsertTill's message field is mostly a clean, cashier-readable string
  // ("cannot close the store X because there are open tills", "This Till is already
  // closed") but live data also showed it occasionally leak a raw Java exception
  // string (e.g. "org.springframework.dao.IncorrectResultSizeDataAccessException:
  // ..."). Fall back to a generic message instead of showing that verbatim.
  const sanitizeSyncErrorMessage = (message) => {
    if (!message || /Exception|^[a-zA-Z]+(\.[a-zA-Z0-9_]+){2,}/.test(message)) {
      return "Couldn't close — please try again. Contact support if this continues.";
    }
    return message;
  };

  // if (!tillData || !tillSession) {
  //   return null;
  // }

  return (
    <div
      style={{
        ...data.openTill.mainContainer,
        backgroundColor: "#F3F4F9",
        fontSize: "1vw",
      }}
    >
      <div>
        <Row style={{ height: "6vh", padding: "1% 0 0 1%" }}>
          <Col span={11}>
            <label
              style={{
                fontSize: "1.8em",
                color: "#0F0718",
                letterSpacing: "0.36px",
                opacity: 1,
                fontWeight: "500",
                textAlign: "center",
                paddingLeft: "2vw",
              }}
            >
              {getMultilingualText("Close POS Till")}
            </label>
          </Col>
          <Col
            span={13}
            style={{
              display: "flex",
              alignItems: "end",
              flexDirection: "column",
              padding: "0 1vw",
            }}
          >
            <span style={{ fontWeight: "500", fontSize: "1em", color: "#0F0718" }}>{currentDate}</span>
            <span style={{ fontWeight: "500", fontSize: "1em", color: "#0F0718" }}>{displayClock}</span>
          </Col>
        </Row>

        <div style={{ ...data.openTill.mainCardContainer }}>
          <div
            style={{
              padding: "1vw",
              display: "flex",
              paddingBottom: "0",
              fontSize: "1vw",
            }}
          >
            <div
              style={{
                borderRadius: "5px",
                width: "75%",
                backgroundColor: "#fff",
                height: "84vh",
              }}
            >
              <div
                style={{
                  height: "7vh",
                  width: "100%",
                  backgroundColor: "rgba(146, 144, 152, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                }}
              >
                <span
                  style={{
                    fontSize: "1.25em",
                    fontWeight: 500,
                    paddingLeft: "3vw",
                    verticalAlign: "middle",
                    color: "#0F0718",
                  }}
                >
                  {getMultilingualText("Select Cash To Keep")}
                </span>
              </div>
              <div style={{ padding: "2vh 3vw" }}>
                <div style={{ marginBottom: 16 }} />
                <Checkbox disabled={cashIndex} value="0" onChange={onChange} checked={checkedValue === "0"} style={{ fontSize: "1vw", transform: "scale(1.3)" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "1.2em",
                      color: "#0F0718",
                    }}
                  >
                    {useSessionStore.getState().tillSession?.totalOpeningAmount}
                  </span>
                </Checkbox>
              </div>
            </div>
            <div
              style={{
                width: "25%",
                paddingLeft: "2.3%",
                fontSize: "1vw",
                position: "relative",
              }}
            >
              <div style={{ ...data.openTill.opentillRightCard }}>
                <Input
                  disabled={posConfig?.manualCashToKeep === "Y" && checkedValue !== "Nothing" ? false : true}
                  ref={inputRef}
                  id="issueCardAmount"
                  placeholder="Enter Amount"
                  size="large"
                  allowClear={false}
                  value={cashToKeep}
                  // onChange={(e) => setCashToKeep(e.target.value)}
                  onChange={handleCashToKeepChange}
                  style={{
                    ...retailStyles.posScreenStyles.orderHistoryModal.closeTillSearchInput,
                    padding: "1vh 1.5vw",
                    height: "9vh",
                    marginBottom: "10px",
                  }}
                />
              </div>
              <Row style={{ height: "", fontSize: "1vw", paddingTop: "0.4vh" }} gutter={[8, 6]}>
                <Col span={8}>
                  <button
                    id="sm-amount-button1"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("1");
                      e.preventDefault();
                    }}
                  >
                    1
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button2"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("2");
                      e.preventDefault();
                    }}
                  >
                    2
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button3"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("3");
                      e.preventDefault();
                    }}
                  >
                    3
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button4"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("4");
                      e.preventDefault();
                    }}
                  >
                    4
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button5"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("5");
                      e.preventDefault();
                    }}
                  >
                    5
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button6"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("6");
                      e.preventDefault();
                    }}
                  >
                    6
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button7"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("7");
                      e.preventDefault();
                    }}
                  >
                    7
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button8"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("8");
                      e.preventDefault();
                    }}
                  >
                    8
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button0"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("9");
                      e.preventDefault();
                    }}
                  >
                    9
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button0"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("0");
                      e.preventDefault();
                    }}
                  >
                    0
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button00"
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount(".");
                      e.preventDefault();
                    }}
                  >
                    .
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    id="sm-amount-button."
                    className="amt-dial-btn-cashtoKeep"
                    onClick={(e) => {
                      handleAddAmount("x");
                      e.preventDefault();
                    }}
                  >
                    <img src={CancelArrow} alt="back space" width={20} height={15} />
                  </button>
                </Col>
                <Col span={8}>
                  <button
                    onClick={(e) => {
                      handleAddAmount("clear");
                    }}
                    id="sm-amount-addQuantity"
                    className="amt-dial-btn-cashtoKeep"
                  >
                    Clear
                  </button>
                </Col>
                <Col span={16}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    id="sm-amount-addQuantity"
                    className="amt-dial-btn-cashtoKeep"
                  >
                    Enter
                  </button>
                </Col>
                <Col span={24} style={{ paddingTop: "1vh" }}>
                  <button
                    onClick={(e) => {
                      if (posConfig?.showCloseTillPaymentSummary === "Y") {
                        history.push("/close-till-all");
                      } else if (posConfig?.showDenominations === "Y") {
                        history.push("/close-till");
                      } else {
                        history.push("/pos");
                      }
                    }}
                    id="sm-amount-addQuantity"
                    className="amt-dial-btn-cashtoKeep-back"
                  >
                    {getMultilingualText("Back")}
                  </button>
                </Col>
                <Col
                  span={24}
                  style={{
                    width: "94%",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  {posConfig?.disableStoreCloseOptionInLogout !== "Y" && !isFutureBusinessDateSession ? (
                    <Button
                      onClick={(e) => {
                        Modal.confirm({
                          title: "Confirm Close Store & Shift",
                          icon: null,
                          content: <div>Are you sure you want to close the store and your shift?</div>,
                          cancelText: "Cancel",
                          okText: "Confirm",
                          okButtonProps: { style: { display: "inline-block" } },
                          autoFocusButton: null,
                          onOk() {
                            // BUG-1239: Always close shift + sync first. Day End app
                            // opens automatically via doLogout in tillCloseService when
                            // advanceDayClose === "Y" and closeStoreFlag is true.
                            runCloseAction(true);
                          },
                        });
                      }}
                      id="sm-amount-closeStore"
                      loading={closingAction === "store"}
                      disabled={isButtonDisabled}
                      style={{
                        flex: 1,
                        minHeight: multilingual ? "7vh" : "6vh",
                        borderRadius: "4px",
                        background: "#2f3856",
                        border: "none",
                        fontSize: multilingual ? "" : "1.25em",
                        fontWeight: "500",
                        color: "#fff",
                        cursor: isButtonDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {closingAction === "store" ? "Closing..." : "Close Store"}
                      {multilingual && closingAction !== "store" && <span style={{ display: "block" }}>{t("Close Store")}</span>}
                    </Button>
                  ) : null}
                  {posConfig?.disableShiftCloseInLogout !== "Y" ? (
                    <Button
                      onClick={(e) => {
                        if (navigator.onLine) {
                          runCloseAction(false);
                        } else {
                          errorMessage("Sync Failed", "Please check your network connection and try again");
                        }
                      }}
                      loading={closingAction === "shift"}
                      disabled={isButtonDisabled}
                      style={{
                        flex: 1,
                        minHeight: multilingual ? "7vh" : "6vh",
                        borderRadius: "4px",
                        background: "#2f3856",
                        border: "none",
                        fontSize: multilingual ? "" : "1.25em",
                        fontWeight: "500",
                        color: "#fff",
                        cursor: isButtonDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {closingAction === "shift" ? "Closing..." : "Close Shift"}
                      {multilingual && closingAction !== "shift" && <span style={{ display: "block" }}>{t("Close Shift")}</span>}
                    </Button>
                  ) : null}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={isIframeModalVisible}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width="90%"
        styles={{ body: { height: "85vh", padding: 0 } }}
        destroyOnClose
        centered
        title="Day End Application"
      >
        <iframe src={iframeUrl} style={{ width: "100%", height: "100%", border: "none" }} title="Day End Application" />
      </Modal>
    </div>
  );
};

export default CashToKeep;
